/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// React
import { useCallback, useEffect, useState } from 'react';

// React dropzone
import {
	FileError,
	FileRejection,
	useDropzone,
} from 'react-dropzone';

// Mantine
import {
	Box,
	Image,
	rem,
	Stack,
	Text,
} from '@mantine/core';

// Components
import SingleFileUploadWithProgress from './SingleFileUploadWithProgress';
import ErrorSingleFileUploadWithProgress from './ErrorSingleFileUploadWithProgress';

// Styles
import classes from './DropzoneMultipleFiles.module.css';
import clsx from 'clsx';

// Icons
import UploadIcon from '@assets/icons/upload.svg';

// I18n
import { useTranslation } from 'react-i18next';

// Configs
import {
	ACCEPTED_MIME_TYPES,
	MAX_SIZE_BYTES,
} from './config';

// Constants
import { NOT_APPLICABLE } from '@config/constants/common';
import { randomId } from '@mantine/hooks';
import { cloneDeep } from '@utils/functions';
import SingleFileApiUploadWithProgress from './SingleFileApiUploadWithProgress';

/* -------------------------------------------------------------------------- */
/*                                  Interface                                 */
/* -------------------------------------------------------------------------- */
export interface UploadableFile {
	id: number;
	file: File;
	errors: FileError[];
	url?: string;
}

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */

type TDropzoneMultipleFilesCreateProps = {
	/** Determines whether a loading overlay should be displayed over the dropzone */
	getUploadStatus: (status: boolean) => void;
	/** Called when valid files are uploaded to the dropzone */
	handleUpload: (files: any) => void;
	/** Called when valid files are updated to the dropzone */
	handleReload: (files: any) => void;
	/** Label of the form control */
	label?: string;
	/** Entity of file for upload single file */
	entity?: string;
	/** Type of file public or not 'true' or 'false' */
	isPublicFile?: boolean;
	/************/
	fileList?: any[];
	apiUploadFile?: string;
	documentId?: string;
	route?: string;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const DropzoneMultipleFiles = (
	props: TDropzoneMultipleFilesCreateProps
) => {
	/* ******************************** PROPS ******************************* */
	const {
		isPublicFile,
		entity,
		label,
		getUploadStatus,
		handleReload,
		documentId,
		route,
	} = props;

	/* ******************************** I18N ******************************* */
	const { t } = useTranslation();

	/* ******************************** STATES ******************************* */
	const [filesWithStatus, setFilesWithStatus] = useState(
		[]
	);
	const [files, setFiles] = useState<any[]>([]);

	const [operation, setOperation] = useState<
		null | string
	>(null);

	/* ******************************** HELPERS ******************************* */

	// Function to upload file & show on dropzone
	const handleUpload = (file: File) => {
		setFiles((curr) =>
			curr.map((prev) => {
				if (prev.file === file) {
					return { ...prev };
				}
				return prev;
			})
		);
	};

	// Function to delete the file in case of upload failure
	const handleDelete = (file: File) => {
		setFiles((curr) =>
			curr.filter((prev) => prev.file !== file)
		);
	};

	// Function to get status of each file
	const updateAllStatus = (newObject: any) => {
		setOperation('upload');
		setFilesWithStatus((prevStatus) => {
			const updatedStatus: any = [...prevStatus];
			const entries = Object.entries(newObject);
			entries.forEach(([key, value]) => {
				const index = updatedStatus.findIndex(
					(item: any) => Object.keys(item)[0] === key
				);
				if (index > -1) {
					updatedStatus[index] = {
						[key]: value,
					};
				} else {
					updatedStatus.push({ [key]: value });
				}
			});
			return updatedStatus;
		});
	};

	// Function to check if all items in the array have their 'isLoading' property set to false
	const areAllItemsNotLoading = (
		array: { [key: string]: { isLoading: boolean } }[]
	): boolean => {
		if (array.length === 0) {
			return true;
		}

		return array.every(
			(item) => !item[Object.keys(item)[0]]?.isLoading
		);
	};

	/* ******************************** HOOKS ******************************* */
	useEffect(() => {
		if (filesWithStatus && operation == 'upload') {
			getUploadStatus(
				areAllItemsNotLoading(filesWithStatus)
			);
			if (areAllItemsNotLoading(filesWithStatus)) {
				const keysAndIds = filesWithStatus.map((item) => {
					const filename = Object.keys(item)[0];

					const dataFound =
						props.fileList &&
						Array.isArray(props.fileList)
							? props.fileList.find(
									(item: any) => item.filename == filename
								)
							: null;

					const value: any = Object.values(item)[0];

					const id = value?.data?.id
						? value?.data?.id
						: dataFound && dataFound.id
							? dataFound.id
							: '';

					return { filename, id };
				});

				props.handleUpload(keysAndIds);
			}
		}
	}, [filesWithStatus]);

	useEffect(() => {
		if (props.fileList) {
			const cloneFileList = cloneDeep(props.fileList);
			const newFiles = cloneFileList.map((item: any) => {
				return {
					file: {
						name: item.filename,
						path: item.filename,
					},
					errors: [],
					id: item.id,
					isToUpdate: true,
				};
			});
			setFiles(newFiles);
		}
	}, [props.fileList]);

	const onDrop = useCallback(
		(_accFiles: File[], rejFiles: FileRejection[]) => {
			const mappedAcc = _accFiles.map((file) => ({
				file,
				errors: [],
				id: randomId(),
			}));

			const mappedRej = rejFiles.map((r) => ({
				...r,
				id: randomId(),
			}));

			setFiles((curr) => [
				...curr,
				...mappedAcc,
				...mappedRej,
			]);
		},
		[]
	);

	// Enum for error codes
	enum ErrorCode {
		DuplicatedFile = 'duplicated-uploaded-files',
	}

	// File duplication validation function
	const validateFileDuplication = (
		file: File
	): { code: string; message: string } | null => {
		const isFileDuplicated = files.some(
			(item) => item.file.name === file.name
		);

		if (isFileDuplicated) {
			return {
				code: ErrorCode.DuplicatedFile,
				message: NOT_APPLICABLE,
			};
		}

		return null;
	};

	const { getRootProps, getInputProps } = useDropzone({
		onDrop,
		accept: ACCEPTED_MIME_TYPES,
		maxSize: MAX_SIZE_BYTES,
		validator: (val: any) => validateFileDuplication(val),
	});

	/* ******************************** CONSTANTS ******************************* */
	const MIME_TYPES =
		typeof ACCEPTED_MIME_TYPES === 'object' &&
		!Array.isArray(ACCEPTED_MIME_TYPES) &&
		ACCEPTED_MIME_TYPES !== null &&
		Object.values(ACCEPTED_MIME_TYPES).flat().join(', ');

	/* ******************************** RENDERING ******************************* */

	return (
		<Box className={classes.container}>
			<Stack gap={'5px'}>
				<Text span className={classes.label}>
					{label}
				</Text>
				<Text span className={classes.description}>
					{`${t('errors.fileTypesMustBe')} ${MIME_TYPES}`}
				</Text>
			</Stack>
			<Box
				data-testid="D2DtDXR7_Z5jdHi6B-dropzone"
				{...getRootProps({
					className: classes.dropzone,
				})}
				className={classes.dropzone}
			>
				<input {...getInputProps()} />
				<Image
					style={{
						width: rem(52),
						height: rem(52),
						color: 'var(--mantine-color-dimmed)',
					}}
					src={UploadIcon}
				/>
				<Text className={classes.text}>
					{t('form.dragAndDropFiles')}
					<Text
						span
						c="blue"
						inherit
						className={clsx(
							classes.text,
							classes.underline
						)}
					>
						{t('global.upload')}.
					</Text>
				</Text>
				{Array.isArray(files) && files.length > 0 && (
					<Text className={classes.text}>
						<Text
							span
							inherit
							className={clsx(classes.text)}
						>
							{t('form.youCanUploadMoreFiles')}
						</Text>
					</Text>
				)}
			</Box>
			<Box className={classes.containerFiles}>
				{files.map((fileWrapper: any) => (
					<Box key={fileWrapper.id}>
						{fileWrapper &&
						fileWrapper.errors &&
						Array.isArray(fileWrapper.errors) &&
						fileWrapper.errors.length ? (
							<ErrorSingleFileUploadWithProgress
								onDelete={handleDelete}
								onUpload={handleUpload}
								onReload={() => {
									if (Array.isArray(files)) {
										setFiles(
											files.filter(
												(_file) =>
													_file.id !== fileWrapper.id
											)
										);
									}
								}}
								file={fileWrapper.file}
								errors={fileWrapper.errors}
							/>
						) : props.apiUploadFile ? (
							<SingleFileApiUploadWithProgress
								apiUploadFile={props.apiUploadFile}
								onGetUploadFileStatus={updateAllStatus}
								entity={entity}
								isPublicFile={isPublicFile}
								route={route}
								documentId={documentId}
								onDelete={handleDelete}
								onUpload={handleUpload}
								onReload={() => {
									setOperation('reload');
									if (Array.isArray(files)) {
										setFiles(
											files.filter(
												(_file) =>
													_file.id !== fileWrapper.id
											)
										);
									}
									setFilesWithStatus(
										filesWithStatus.filter((item) => {
											return (
												Object.keys(item)[0] !==
												fileWrapper.file.path
											);
										})
									);

									const cloneFiles = cloneDeep(files);

									const keysAndIds = cloneFiles
										.filter(
											(_file: any) =>
												_file.id !== fileWrapper.id
										)
										.map((item: any) => {
											const id =
												item && item.id ? item.id : '';
											return {
												filename: item.file.name,
												id,
											};
										});

									handleReload(keysAndIds);
								}}
								file={fileWrapper.file}
								id={
									fileWrapper && fileWrapper.id
										? fileWrapper.id
										: ''
								}
								isToUpdate={
									fileWrapper && fileWrapper.isToUpdate
								}
							/>
						) : (
							<SingleFileUploadWithProgress
								onGetStatus={(status) => {
									setOperation('upload');
									updateAllStatus(status);
								}}
								entity={entity}
								isPublicFile={isPublicFile}
								route={route}
								documentId={documentId}
								onDelete={handleDelete}
								onUpload={handleUpload}
								onReload={() => {
									setOperation('reload');
									if (Array.isArray(files)) {
										setFiles(
											files.filter(
												(_file) =>
													_file.id !== fileWrapper.id
											)
										);
									}
									setFilesWithStatus(
										filesWithStatus.filter((item) => {
											return (
												Object.keys(item)[0] !==
												fileWrapper.file.path
											);
										})
									);
									const keysAndIds = filesWithStatus
										.filter((item) => {
											return (
												Object.keys(item)[0] !==
												fileWrapper.file.path
											);
										})
										.map((item) => {
											const filename =
												Object.keys(item)[0];
											const value: any =
												Object.values(item)[0];
											const id =
												value &&
												value.data &&
												value.data.id
													? value.data.id
													: '';
											return { filename, id };
										});
									handleReload(keysAndIds);
								}}
								file={fileWrapper.file}
								id={
									fileWrapper && fileWrapper.id
										? fileWrapper.id
										: ''
								}
								isToUpdate={
									fileWrapper && fileWrapper.isToUpdate
								}
							/>
						)}
					</Box>
				))}
			</Box>
		</Box>
	);
};
